import { HEADER_SHAPES } from "@keepoinc/site-page-react-component";
import { CSSProperties, KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";

import { classNames } from "../../../../../../utils/styles";
import { PickerContainer } from "../PatternPicker";

/** The classic photo's frames: round (none) or one of the shapes. */
export const FRAMES = ["round", ...HEADER_SHAPES] as const;
export type Frame = (typeof FRAMES)[number];

type Props<T extends string> = {
  /** The field's name ("Formato", "Acabamento"). */
  label: string;
  options: readonly T[];
  value: T;
  name: (option: T) => string;
  /** What each option shows (the photo in a frame, with a finish), inside a sample of the page's color. */
  draw: (option: T) => ReactNode;
  /** The page's color, behind the samples (as on the Keepo). */
  pageColor: string;
  onPick: (option: T) => void;
};

/**
 * A choice shown as samples (as the Grafismo's "Desenho"): the one chosen and its name, opening every option. The
 * top's "Formato" and "Acabamento".
 */
export const SamplePicker = <T extends string>({ label, options, value, name, draw, pageColor, onPick }: Props<T>) => {
  const container = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLButtonElement>(null);
  const [isOpen, setOpen] = useState(false);
  const sample: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: pageColor,
  };

  // closes on a click outside it
  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // opened: the frame chosen takes the focus
  useEffect(() => {
    if (isOpen) container.current?.querySelector<HTMLButtonElement>('.drawings [aria-checked="true"]')?.focus();
  }, [isOpen]);

  const close = () => {
    setOpen(false);
    field.current?.focus();
  };

  const keyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      close();
    }
  };

  return (
    <PickerContainer ref={container} onKeyDown={keyDown}>
      <span className="role" aria-hidden="true">
        {label}
      </span>
      <button
        ref={field}
        type="button"
        className="value"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`${label}: ${name(value)}`}
        onClick={() => setOpen(!isOpen)}>
        <span className="swatch" style={sample}>
          {draw(value)}
        </span>
        {name(value)}
      </button>
      {isOpen && (
        <div className="panel" role="group" aria-label={label}>
          <div className="drawings" role="radiogroup" aria-label={label}>
            {options.map(option => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={option === value}
                aria-label={name(option)}
                title={name(option)}
                className={classNames({ selected: option === value })}
                style={sample}
                onClick={() => {
                  onPick(option);
                  close();
                }}>
                {draw(option)}
              </button>
            ))}
          </div>
        </div>
      )}
    </PickerContainer>
  );
};
